import React, { useState } from 'react';
import { ConditionType, CONDITION_TYPES } from '../types/condition';
import { Button } from './ui/Button';
import { XIcon, AlertCircleIcon } from 'lucide-react';
interface CreateConditionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (type: ConditionType, notes: string) => void;
  targetCount: number;
  targetLabel?: string;
}
export function CreateConditionModal({
  isOpen,
  onClose,
  onSubmit,
  targetCount,
  targetLabel
}: CreateConditionModalProps) {
  const [type, setType] = useState<ConditionType | ''>('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  if (!isOpen) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!type) return;
    setIsSubmitting(true);
    // Simulate network delay for better UX
    setTimeout(() => {
      onSubmit(type, notes);
      setIsSubmitting(false);
      // Reset form
      setType('');
      setNotes('');
      onClose();
    }, 500);
  };
  const title = targetCount === 1 ? `Add Condition for ${targetLabel || 'Record'}` : `Add Condition for ${targetCount} Records`;
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 transition-opacity" onClick={onClose} data-id="element-1" />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-2">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md pointer-events-auto flex flex-col max-h-[90vh]" data-id="element-3">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100" data-id="element-4">
            <div className="flex items-center space-x-2" data-id="element-5">
              <div className="p-2 bg-amber-100 rounded-full" data-id="element-6">
                <AlertCircleIcon className="w-5 h-5 text-amber-600" data-id="element-7" />
              </div>
              <h3 className="text-lg font-bold text-gray-900" data-id="element-8">
                Create Condition
              </h3>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-9">
              <XIcon className="w-5 h-5" data-id="element-10" />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto" data-id="element-11">
            <div className="bg-blue-50 text-blue-800 text-sm px-4 py-3 rounded-md border border-blue-100" data-id="element-12">
              You are creating a condition for{' '}
              <strong data-id="element-13">{targetLabel || `${targetCount} records`}</strong>. This
              will flag the record(s) for further review.
            </div>

            <div className="space-y-1.5" data-id="element-14">
              <label htmlFor="condition-type" className="block text-sm font-medium text-gray-700" data-id="element-15">
                Condition Type <span className="text-red-500" data-id="element-16">*</span>
              </label>
              <select id="condition-type" value={type} onChange={e => setType(e.target.value as ConditionType)} required className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-3 border" data-id="element-17">
                <option value="" disabled data-id="element-18">
                  Select a type...
                </option>
                {CONDITION_TYPES.map(t => <option key={t} value={t} data-id="element-19">
                    {t}
                  </option>)}
              </select>
            </div>

            <div className="space-y-1.5" data-id="element-20">
              <label htmlFor="condition-notes" className="block text-sm font-medium text-gray-700" data-id="element-21">
                Notes
              </label>
              <textarea id="condition-notes" value={notes} onChange={e => setNotes(e.target.value)} rows={4} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-3 border resize-none" placeholder="Add details about this condition..." data-id="element-22" />
            </div>

            {/* Footer */}
            <div className="pt-4 flex items-center justify-end space-x-3" data-id="element-23">
              <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting} data-id="element-24">
                Cancel
              </Button>
              <Button type="submit" variant="primary" isLoading={isSubmitting} disabled={!type} data-id="element-25">
                Create Condition
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>;
}